/** Pure transforms for sector GHG chart data - CP-WP-006 null semantics. */

export type SectorChartPoint = {
  year: number;
  lightDutyVehicles: number;
  heavyDutyVehicles: number;
  naturalGasResidential: number;
  electricityCommercial: number;
  electricityResidential: number;
  naturalGasCommercial: number;
  waste: number;
  /** Null when wastewater is unavailable for that year (never coerced to zero). */
  wastewater: number | null;
  offroad: number;
};

const DATASET_FIELD_MAP: Record<string, keyof Omit<SectorChartPoint, 'year'>> = {
  'Light Duty Vehicles': 'lightDutyVehicles',
  'Heavy Duty Vehicles': 'heavyDutyVehicles',
  'Natural Gas Residential': 'naturalGasResidential',
  'Electricity Commercial': 'electricityCommercial',
  'Electricity Residential': 'electricityResidential',
  'Natural Gas Commercial': 'naturalGasCommercial',
  Waste: 'waste',
  'Solid Waste': 'waste',
  Wastewater: 'wastewater',
  Offroad: 'offroad',
};

function isWastewaterLabel(label: string): boolean {
  return label === 'Wastewater' || label.startsWith('Wastewater ');
}

function parseNumeric(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function sectorNumericValue(label: string, raw: unknown): number | null {
  if (isWastewaterLabel(label)) {
    return parseNumeric(raw);
  }
  const n = parseNumeric(raw);
  return n ?? 0;
}

export function chartJsDatasetsToSectorPoints(chartData: {
  labels: unknown[];
  datasets: Array<{ label?: string; data?: unknown[] }>;
}): SectorChartPoint[] {
  const years = chartData.labels;
  const datasets = chartData.datasets ?? [];

  return years.map((yearRaw, index) => {
    const point: SectorChartPoint = {
      year: parseInt(String(yearRaw), 10),
      lightDutyVehicles: 0,
      heavyDutyVehicles: 0,
      naturalGasResidential: 0,
      electricityCommercial: 0,
      electricityResidential: 0,
      naturalGasCommercial: 0,
      waste: 0,
      wastewater: null,
      offroad: 0,
    };

    for (const dataset of datasets) {
      const label = String(dataset.label ?? '');
      let field = DATASET_FIELD_MAP[label];
      if (!field && isWastewaterLabel(label)) {
        field = 'wastewater';
      }
      if (!field) continue;
      const value = sectorNumericValue(label, dataset.data?.[index]);
      if (field === 'wastewater') {
        point.wastewater = value;
      } else {
        point[field] = value ?? 0;
      }
    }

    return point;
  });
}

export function wastewaterSeriesValues(points: SectorChartPoint[]): Array<number | null> {
  return points.map((d) => d.wastewater);
}

export function hasWastewaterSeries(points: SectorChartPoint[]): boolean {
  return points.some((d) => d.wastewater != null);
}

export function sumAvailableSectorsForYear(point: SectorChartPoint): number {
  let total =
    point.lightDutyVehicles +
    point.heavyDutyVehicles +
    point.naturalGasResidential +
    point.electricityCommercial +
    point.electricityResidential +
    point.naturalGasCommercial +
    point.waste +
    point.offroad;
  if (point.wastewater != null) {
    total += point.wastewater;
  }
  return total;
}

/** Sum all chart-visible sectors for the inventory year, including carried-forward wastewater estimates. */
export function sumInventoryYearChartTotal(
  chartData: { labels: unknown[]; datasets: Array<{ label?: string; data?: unknown[] }> },
  yearIndex: number,
  _wastewaterSourceDataYear?: number | null
): number {
  let total = 0;
  for (const dataset of chartData.datasets ?? []) {
    const label = String(dataset.label ?? '');
    const raw = dataset.data?.[yearIndex];
    if (isWastewaterLabel(label)) {
      const value = parseNumeric(raw);
      if (value != null) total += value;
      continue;
    }
    const value = sectorNumericValue(label, raw);
    if (value != null) total += value;
  }
  return total;
}

/** Diagnostic only: total excluding carried-forward wastewater when source year differs from inventory year. */
export function diagnosticTotalExcludingWastewaterEstimate(
  chartData: { labels: unknown[]; datasets: Array<{ label?: string; data?: unknown[] }> },
  yearIndex: number,
  wastewaterSourceDataYear?: number | null
): number {
  const inventoryYear = Number(chartData.labels?.[yearIndex]);
  let total = 0;
  for (const dataset of chartData.datasets ?? []) {
    const label = String(dataset.label ?? '');
    const raw = dataset.data?.[yearIndex];
    if (isWastewaterLabel(label)) {
      if (
        wastewaterSourceDataYear != null &&
        Number.isFinite(inventoryYear) &&
        wastewaterSourceDataYear !== inventoryYear
      ) {
        continue;
      }
      const value = parseNumeric(raw);
      if (value != null) total += value;
      continue;
    }
    const value = sectorNumericValue(label, raw);
    if (value != null) total += value;
  }
  return total;
}

export function hasRealInventoryChartData(
  ghgData: { chartData?: { labels?: unknown[]; datasets?: unknown[] } } | null | undefined
): boolean {
  const labels = ghgData?.chartData?.labels;
  const datasets = ghgData?.chartData?.datasets;
  return Boolean(labels?.length && datasets?.length);
}

export type SectorGhgDisplayState = 'loading' | 'error' | 'unavailable' | 'ready';

export function resolveSectorGhgDisplayState(input: {
  loading: boolean;
  error: string | null;
  ghgData: { chartData?: { labels?: unknown[]; datasets?: unknown[] } } | null | undefined;
}): SectorGhgDisplayState {
  if (input.loading) return 'loading';
  if (input.error) return 'error';
  if (!hasRealInventoryChartData(input.ghgData)) return 'unavailable';
  return 'ready';
}
